// @flow
import React, { useLayoutEffect, useRef, useState } from 'react';
import cn from 'classnames';
import styles from './styles.module.scss';

type FeedbackEvent = { id: number, kind: 'correct' | 'wrong' | 'milestone' };
type Props = {
  soundEnabled: boolean,
  animationsEnabled: boolean,
  reducedMotion: boolean,
  streak: number,
  event: ?FeedbackEvent,
  onToggleSound: () => void,
  onToggleAnimations: () => void,
};

const SoundIcon = ({ enabled }: { enabled: boolean }) => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
    <path d="M10.5 5.5 6.5 9H3.5v6h3l4 3.5v-13Z" />
    {enabled ? (
      <>
        <path d="M14 8.5a5 5 0 0 1 0 7" />
        <path d="M17 5.5a9 9 0 0 1 0 13" />
      </>
    ) : (
      <path d="m15.5 9 5 6m0-6-5 6" />
    )}
  </svg>
);

const MotionIcon = ({ enabled }: { enabled: boolean }) => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
    <path d="m14.5 7.5 1.8 5.2 5.2 1.8-5.2 1.8-1.8 5.2-1.8-5.2-5.2-1.8 5.2-1.8 1.8-5.2Z" />
    <path d="M6 2.5 7 5l2.5 1L7 7l-1 2.5L5 7 2.5 6 5 5 6 2.5Z" />
    {enabled && <path d="M4 15.5v4M2 17.5h4" />}
  </svg>
);

const FlameIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path d="M13.2 2.3c.8 4.6-3.5 5.7-3.1 9.5-1.6-.7-2.4-2.2-2.4-4.1C5 10.3 3.7 12.9 4.3 16c.7 3.6 3.5 5.9 7.4 5.9 4.6 0 7.9-3.2 7.9-7.6 0-4.9-3-8.6-6.4-12Z" />
    <path className={styles.flameCore} d="M12.4 12.1c.1 2.1-2.9 3.3-2.9 5.4 0 1.4 1 2.4 2.4 2.4 1.6 0 2.7-1.2 2.7-2.8 0-1.8-.9-3.5-2.2-5Z" />
  </svg>
);

const TrainerFeedback = ({
  soundEnabled,
  animationsEnabled,
  reducedMotion,
  streak,
  event,
  onToggleSound,
  onToggleAnimations,
}: Props) => {
  const motionActive = animationsEnabled && !reducedMotion;
  const lastEventId = useRef(null);
  const previousStreak = useRef(0);
  const [numberAnimation, setNumberAnimation] = useState({
    id: null, active: false, celebrate: false,
  });

  useLayoutEffect(() => {
    const newEvent = event && event.id !== lastEventId.current;
    const grew = streak > previousStreak.current;
    if (event) lastEventId.current = event.id;
    previousStreak.current = streak;
    const correctEvent = event?.kind === 'correct' || event?.kind === 'milestone';
    if (motionActive && newEvent && grew && correctEvent) {
      setNumberAnimation({ id: event.id, active: true, celebrate: event.kind === 'milestone' });
    } else if (!motionActive || !event || streak === 0) {
      setNumberAnimation((previous) => (previous.active || previous.celebrate
        ? { ...previous, active: false, celebrate: false } : previous));
    }
  }, [event, motionActive, streak]);

  const celebrate = motionActive && numberAnimation.celebrate && event?.kind === 'milestone';
  const animationDescription = reducedMotion
    ? 'Animations are paused by your reduced motion setting.'
    : `Animations: ${animationsEnabled ? 'on' : 'off'}. Turn animations ${animationsEnabled ? 'off' : 'on'}.`;

  return (
    <div className={styles.toolbar} data-trainer-feedback>
      <div className={styles.streakSlot}>
        {streak > 0 && (
          <div
            className={cn(styles.streak, { [styles.milestone]: celebrate })}
            title={`${streak} correct decisions in a row this session`}
            aria-label={`${streak} correct decisions in a row this session`}
            role="status"
            aria-live="polite"
            aria-atomic="true"
          >
            {celebrate && (
              <span key={event?.id} className={styles.chipBurst} aria-hidden="true">
                <i />
                <i />
                <i />
                <i />
                <i />
              </span>
            )}
            <span className={styles.streakContent}>
              <span className={styles.flame}><FlameIcon /></span>
              <span className={styles.streakReadout}>
                <strong
                  key={numberAnimation.id}
                  className={cn(styles.count, { [styles.countAnimated]: numberAnimation.active })}
                  data-streak-count
                  onAnimationEnd={() => {
                    setNumberAnimation((previous) => ({ ...previous, active: false }));
                  }}
                >
                  {streak}
                </strong>
                <span className={styles.streakLabel} aria-hidden="true">
                  Streak
                </span>
              </span>
            </span>
          </div>
        )}
      </div>
      <div className={styles.controls} role="group" aria-label="Training feedback settings">
        <button
          type="button"
          className={cn(styles.toggle, { [styles.enabled]: soundEnabled })}
          onClick={onToggleSound}
          aria-label="Sound effects"
          aria-pressed={soundEnabled}
          title={`Sound: ${soundEnabled ? 'on' : 'off'}. Turn sound ${soundEnabled ? 'off' : 'on'}.`}
        >
          <SoundIcon enabled={soundEnabled} />
        </button>
        <button
          type="button"
          className={cn(styles.toggle, { [styles.enabled]: motionActive })}
          onClick={onToggleAnimations}
          aria-label="Animations"
          aria-pressed={animationsEnabled}
          title={animationDescription}
        >
          <MotionIcon enabled={motionActive} />
        </button>
      </div>
    </div>
  );
};

export default TrainerFeedback;
